import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useTranslation } from "react-i18next";

import {
  calculateTapped,
  heightChanged,
  resultCopyTapped,
  selectState,
  weightChanged,
} from "./BmiCalculatorSlice";
import {
  Box,
  InputAdornment,
  TextField,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import { ResultCard } from "../../components/result_card/ResultCard";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { EqualColumnTable } from "../../components/equal_column_table/EqualColumnTable";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2";
import { HomeCard } from "../../components/home_card/HomeCard";
import WeightUnitConverterImage from "../../assets/WeightUnitConverter.webp";
import TemperatureConverterImage from "../../assets/TemperatureConverter.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function BmiCalculator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("bmiCalculator.title")}
      pageSeoTitle={t("bmiCalculator.seoTitle")}
      pageDescription={t("bmiCalculator.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("bmiCalculator.mainHeading")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("bmiCalculator.heightLabel")}
            variant="outlined"
            color="info"
            value={state.heightCm || ""}
            onChange={(e) => dispatch(heightChanged(e.target.value))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>cm</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("bmiCalculator.weightLabel")}
            variant="outlined"
            color="info"
            value={state.weightKg || ""}
            onChange={(e) => dispatch(weightChanged(e.target.value))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>kg</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 1, mb: 3 }}>
          <CustomButton
            label={t("bmiCalculator.calculateButton")}
            onClick={() => {
              dispatch(calculateTapped([state.weightKg, state.heightCm]));
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.bmiResult}`}
            symbol="kg/m²"
            description={state.bmiDescription}
            onCopy={() => dispatch(resultCopyTapped())}
            resultCopied={state.resultCopied}
          />
        </Grid2>

        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>

        {/* SEO Content */}
        <Grid2 xs={12}>
          <AdSense
              adSlot={ADSENSE_SLOTS.IN_ARTICLE}
              adFormat="fluid"
              adLayoutKey="-fb+5w+4e-db+86"
              sx={{ mb: 3 }}
            />
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("bmiCalculator.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("bmiCalculator.content.freeDescription")}
          </Typography>

          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("bmiCalculator.content.whatIsHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("bmiCalculator.content.whatIsDescription")}
          </Typography>

          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("bmiCalculator.content.categoriesHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("bmiCalculator.content.categoriesDescription")}
          </Typography>

          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("bmiCalculator.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("bmiCalculator.content.howToDescription")}
          </Typography>
        </Grid2>

        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("bmiCalculator.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="weightConverter"
            link="/WeightUnitConverter"
            smallCard={true}
            image={WeightUnitConverterImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="temperatureConverter"
            link="/TemperatureConverter"
            smallCard={true}
            image={TemperatureConverterImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
